import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxCurrencyConfig, NgxCurrencyDirective } from 'ngx-currency';
import { Subscription } from 'rxjs';

import { ConfirmModalComponent } from '../../../../../../shared/components/confirm-modal/confirm-modal.component';
import { PercentualProgressBarComponent } from '../../../../../../shared/components/percentual-progress-bar/percentual-progress-bar.component';
import {
    RateioConfigPickerModalComponent,
    RateioConfigPickerResultItem,
} from '../../../../../../shared/components/rateio-config-picker-modal/rateio-config-picker-modal.component';
import { SelectPesquisavelComponent } from '../../../../../../shared/components/select-pesquisavel/select-pesquisavel.component';
import { RateioCentroCusto } from '../../../../../../shared/services/models/financas/rateio-centro-custo.model';
import { LookupItemStruct } from '../../../../../../shared/services/structs/vendas/lookup-item.struct';
import { distribuirRateioEmCentavos, percentualDoRateio, somarPercentuaisDoRateio } from '../venda-pagamento.calculo';
import { montarFormRateio } from '../venda-pagamento.form';

/** O que o picker de rateio e o select de centro esperam: `idCentroCusto` + `nome`. */
interface CentroCustoOpcao {
    idCentroCusto: number;
    nome: string;
}

/**
 * Centro de custo e rateio do documento (§8.10).
 *
 * É o bloco do lançamento do Finanças — `ContasReceberCentroCustoComponent` — refeito sobre o
 * form da VENDA, porque aquele componente amarra os caminhos `dadosGerais.isRateio` e
 * `rateio.itens` do form de contas a receber. Os blocos compartilhados são os MESMOS (picker de
 * configuração, barra de percentual, confirmação, select), então o que o usuário vê é idêntico;
 * o que muda é onde os controles moram: `idCentroCusto`, `isRateioCentroCusto` e
 * `rateioCentroCusto` na raiz do form do documento, que é o que o request carrega.
 *
 * Duas coisas a §8.10 pede além do Finanças, e estão aqui: a conta em CENTAVOS com o resto na
 * última linha, e o mesmo centro não poder aparecer duas vezes (a lista de opções exclui os já
 * escolhidos).
 */
@Component({
    selector: 'app-vd-centro-custo',
    standalone: true,
    imports: [
        ReactiveFormsModule,
        MatButtonModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatSelectModule,
        MatSlideToggleModule,
        MatTooltipModule,
        NgxCurrencyDirective,
        PercentualProgressBarComponent,
        SelectPesquisavelComponent,
    ],
    templateUrl: './vd-centro-custo.component.html',
    styleUrl: './vd-centro-custo.component.css',
})
export class VdCentroCustoComponent implements OnInit, OnChanges, OnDestroy {
    /** O form da RAIZ do documento — os três controles do bloco moram nele. */
    @Input({ required: true }) public form!: FormGroup;

    /** Total do documento: base do espelho valor ↔ percentual. */
    @Input() public totalDoDocumento: number = 0;

    @Input() public centrosCusto: LookupItemStruct[] = [];
    @Input() public rateiosConfigurados: RateioCentroCusto[] = [];
    @Input() public isSomenteLeitura: boolean = false;

    @Input({ required: true }) public mascaraDinheiro!: Partial<NgxCurrencyConfig>;
    @Input({ required: true }) public mascaraPercentual!: Partial<NgxCurrencyConfig>;

    /**
     * Os centros no formato que o picker e o select entendem. Campo, e não getter: o
     * `select-pesquisavel` compara `offlineList` por referência, e um array novo a cada ciclo
     * de detecção o faria reprocessar sem parar.
     */
    public opcoesDeCentro: CentroCustoOpcao[] = [];

    /** O shell recria a seção a cada salvar e o form é dele: a assinatura é cancelada com o componente. */
    private assinaturaDoToggle: Subscription | undefined;

    constructor(
        private fb: FormBuilder,
        private dialog: MatDialog,
    ) {}

    public ngOnInit(): void {
        this.aplicarEstadoDoToggle(this.isRateioAtivo);

        this.assinaturaDoToggle = this.form.get('isRateioCentroCusto')?.valueChanges.subscribe((ativo: boolean) => {
            this.aplicarEstadoDoToggle(ativo);

            // Desligar DESCARTA as linhas (§8.10): o rateio deixa de existir, não fica escondido.
            if (!ativo) this.rateio.clear();
            else if (this.rateio.length === 0) this.adicionarLinha();
        });
    }

    public ngOnDestroy(): void {
        this.assinaturaDoToggle?.unsubscribe();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['centrosCusto']) {
            this.opcoesDeCentro = (this.centrosCusto ?? []).map((c) => ({ idCentroCusto: c.id, nome: c.descricao }));
        }

        // O total mudou (item lançado, frete digitado): os VALORES seguem os percentuais, que são
        // o que o usuário decidiu.
        if (changes['totalDoDocumento'] && !changes['totalDoDocumento'].firstChange) {
            this.recalcularValoresPelosPercentuais();
        }
    }

    /** O `select-pesquisavel` exige `FormControl`; o cast fica aqui, nunca no template. */
    public get idCentroCustoControl(): FormControl {
        return this.form.get('idCentroCusto') as FormControl;
    }

    public get isRateioAtivo(): boolean {
        return this.form.get('isRateioCentroCusto')?.value === true;
    }

    public get rateio(): FormArray {
        return this.form.get('rateioCentroCusto') as FormArray;
    }

    public get linhas(): FormGroup[] {
        return this.rateio.controls as FormGroup[];
    }

    public get somaPercentual(): number {
        return somarPercentuaisDoRateio(this.linhas);
    }

    /**
     * As opções de UMA linha: todos os centros menos os já escolhidos nas OUTRAS linhas (§8.10).
     * A própria linha mantém o seu, senão o select mostraria vazio ao reabrir.
     */
    public opcoesPara(indice: number): CentroCustoOpcao[] {
        const usados = new Set(
            this.linhas
                .filter((_, i) => i !== indice)
                .map((l) => Number(l.get('idCentroCusto')?.value))
                .filter((id) => id > 0),
        );

        return this.opcoesDeCentro.filter((c) => !usados.has(c.idCentroCusto));
    }

    public adicionarLinha(): void {
        this.rateio.push(montarFormRateio(this.fb));
    }

    public removerLinha(indice: number): void {
        this.rateio.removeAt(indice);
    }

    /** Digitou o VALOR: o percentual acompanha, sobre o total do documento. */
    public aoSairDoValor(indice: number): void {
        if (this.totalDoDocumento <= 0) return;

        const linha = this.linhas[indice];
        const valor = Number(linha.get('valor')?.value) || 0;

        linha.get('percentual')?.setValue(percentualDoRateio(valor, this.totalDoDocumento), { emitEvent: false });
    }

    /** Digitou o PERCENTUAL: o valor acompanha. */
    public aoSairDoPercentual(indice: number): void {
        // Um só caminho para "% → R$": o mesmo rateio em centavos com resto na última que a
        // configuração escolhida usa, para Σ R$ fechar o total quando Σ% fecha 100.
        this.recalcularValoresPelosPercentuais();
    }

    /**
     * Aplica uma configuração de rateio já cadastrada (§8.10, "Selecionar configuração de rateio").
     * Com linhas preenchidas, confirma antes de substituir — mesmo diálogo do Finanças.
     */
    public selecionarConfiguracao(): void {
        const temLinhas = this.linhas.some((l) => !!l.get('idCentroCusto')?.value);

        if (!temLinhas) {
            this.abrirPicker();
            return;
        }

        this.dialog
            .open(ConfirmModalComponent, {
                data: {
                    title: 'Substituir rateio atual?',
                    description:
                        'Já existem linhas de rateio preenchidas. Ao selecionar uma configuração, todas as linhas atuais serão substituídas pela configuração escolhida. Deseja continuar?',
                    buttonConfirm: 'Continuar',
                    buttonClose: 'Cancelar',
                },
            })
            .afterClosed()
            .subscribe((result) => {
                if (result?.confirm) this.abrirPicker();
            });
    }

    private abrirPicker(): void {
        this.dialog
            .open(RateioConfigPickerModalComponent, {
                width: '520px',
                data: { configs: this.rateiosConfigurados, centrosCusto: this.opcoesDeCentro },
            })
            .afterClosed()
            .subscribe((itens?: RateioConfigPickerResultItem[]) => {
                if (!itens?.length) return;

                this.rateio.clear();

                for (const item of itens) {
                    const linha = montarFormRateio(this.fb);

                    linha.patchValue({ idCentroCusto: item.idCentroCusto, percentual: item.percentual }, { emitEvent: false });
                    this.rateio.push(linha);
                }

                this.recalcularValoresPelosPercentuais();
            });
    }

    /**
     * Valores a partir dos percentuais, em CENTAVOS e com o resto na ÚLTIMA linha (§8.10) — para
     * a soma dos valores fechar exatamente o total quando os percentuais fecham 100%.
     */
    private recalcularValoresPelosPercentuais(): void {
        if (this.totalDoDocumento <= 0 || !this.linhas.length) return;

        const valores = distribuirRateioEmCentavos(
            this.totalDoDocumento,
            this.linhas.map((l) => Number(l.get('percentual')?.value) || 0),
        );

        this.linhas.forEach((linha, i) => linha.get('valor')?.setValue(valores[i], { emitEvent: false }));
    }

    /** Com o rateio ligado o centro único sai de cena — desabilitado e em cinza (§8.10). */
    private aplicarEstadoDoToggle(ativo: boolean): void {
        const centro = this.idCentroCustoControl;

        if (ativo || this.isSomenteLeitura) {
            if (ativo) centro.setValue(null, { emitEvent: false });
            centro.disable({ emitEvent: false });
        } else {
            centro.enable({ emitEvent: false });
        }
    }
}
